/**
 * P3-28: EldersButtons —— leviathans 面板的 feed / bcoin 按钮组。
 *
 * 迁移自 `classes.diplomacy.ui.EldersPanel.render`
 *（js/diplomacy.js:987-1041）。
 */
import { useCallback, type MouseEvent, type MutableRefObject } from "react";
import type { GamePage } from "../../bridge/GameBridge";
import { getGame } from "../../bridge/GameBridge";
import { t } from "../../bridge/i18n";
import { useGameTick } from "../../bridge/useGameTick";
import { GameButton } from "../../components/GameButton";
import {
  useCrashBcoinButtonModel,
  useModernActionButtonModel,
} from "./useTradeModel";

interface EldersVisibilitySnapshot {
  showBcoin: boolean;
  showCrash: boolean;
}

function computeSnapshot(game: GamePage | null): EldersVisibilitySnapshot | null {
  if (!game) {
    return null;
  }
  const science = game.science as { get: (name: string) => { researched?: boolean } };
  const blackcoin = (game.resPool as { get: (name: string) => { value: number } }).get("blackcoin");
  const workshop = game.workshop as { get: (name: string) => { researched?: boolean } };
  return {
    showBcoin:
      !!science.get("blackchain").researched || (blackcoin?.value ?? 0) > 0,
    showCrash:
      !!science.get("antimatter").researched &&
      !!workshop.get("invisibleBlackHand").researched,
  };
}

function snapshotEq(
  a: EldersVisibilitySnapshot | null,
  b: EldersVisibilitySnapshot | null,
): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  return a.showBcoin === b.showBcoin && a.showCrash === b.showCrash;
}

export function EldersButtons() {
  const visible = useGameTick(
    useCallback((game: GamePage | null) => computeSnapshot(game), []),
    snapshotEq,
  );

  const feedHandler = useCallback(
    (_model?: unknown, event?: unknown) => {
      const e = event as { shiftKey?: boolean; ctrlKey?: boolean; metaKey?: boolean } | undefined;
      const game = getGame() as
        | (GamePage & { diplomacy?: { feedElders: (amt: number) => void } })
        | null;
      const batchSize = e?.shiftKey
        ? 10000
        : e?.ctrlKey || e?.metaKey
          ? (game?.opts as { batchSize?: number } | undefined)?.batchSize ?? 1
          : 1;
      game?.diplomacy?.feedElders(batchSize);
    },
    [],
  );

  const buyHandler = useCallback(() => {
    const game = getGame() as (GamePage & { diplomacy?: { buyBcoin: () => void } }) | null;
    game?.diplomacy?.buyBcoin();
  }, []);

  const sellHandler = useCallback(() => {
    const game = getGame() as (GamePage & { diplomacy?: { sellBcoin: () => void } }) | null;
    game?.diplomacy?.sellBcoin();
  }, []);

  const crashHandler = useCallback(() => {
    const game = getGame() as
      | (GamePage & { calendar?: { correctCryptoPrice: () => void } })
      | null;
    game?.calendar?.correctCryptoPrice();
  }, []);

  const feedModel = useModernActionButtonModel(
    "feedElders",
    t("trade.msg.elders.feed"),
    t("trade.msg.elders.feed.desc"),
    feedHandler,
  );
  const buyModel = useModernActionButtonModel(
    "buyBcoin",
    t("trade.bcoin.buy"),
    t("trade.bcoin.buy.desc"),
    buyHandler,
  );
  const sellModel = useModernActionButtonModel(
    "sellBcoin",
    t("trade.bcoin.sell"),
    t("trade.bcoin.sell.desc"),
    sellHandler,
  );
  const crashModel = useCrashBcoinButtonModel(crashHandler);

  if (!visible) {
    return null;
  }

  return (
    <>
      <ActionButton model={feedModel} />
      {visible.showBcoin && (
        <>
          <ActionButton model={buyModel} />
          <ActionButton model={sellModel} />
        </>
      )}
      {visible.showCrash && <ActionButton model={crashModel} />}
    </>
  );
}

function ActionButton({
  model,
}: {
  model: {
    snap: {
      name: string;
      description: string;
      visible: boolean;
      enabled: boolean;
      resourceIsLimited: string | boolean;
    } | null;
    modelRef: MutableRefObject<Record<string, unknown> | null>;
    controller: { buyItem?: (model: unknown, event?: unknown) => void } | null;
    getTooltip: () => string;
  };
}) {
  const { snap, modelRef, controller, getTooltip } = model;
  if (!snap || !snap.visible) {
    return null;
  }
  return (
    <GameButton
      name={snap.name}
      description={snap.description}
      enabled={snap.enabled}
      resourceIsLimited={snap.resourceIsLimited}
      getTooltip={getTooltip}
      onClick={(event: MouseEvent) => controller?.buyItem?.(modelRef.current, event)}
    />
  );
}
